<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CustomRoleAPI from 'dashboard/api/customRole';
import {
  deliveryPartnerAccountsAPI,
  inventoryWarehousesAPI,
  webOrdersAPI,
  webProductsAPI,
} from 'dashboard/api/webOrder';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import DownloadReportCheckDropdown from './DownloadReportCheckDropdown.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { overviewDateRanges } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  ORDER_SOURCES,
  ORDER_STATUSES,
} from 'dashboard/routes/dashboard/webOrder/helpers/orderFilters';
import {
  CUSTOMER_RANKS,
  DEFAULT_CUSTOMER_RANK,
  DEFAULT_PAYMENT_TYPE,
  DEFAULT_PRODUCT_GRAIN,
  DEFAULT_REPORT_LAYOUT,
  DEFAULT_REPORT_MODE,
  DEFAULT_UPSALE_MODE,
  DOWNLOAD_REPORT_TYPES,
  PAYMENT_TYPES,
  PRODUCT_GRAINS,
  REPORT_LAYOUTS,
  REPORT_MODES,
  UPSALE_MODES,
  customerRankLabelKey,
  dateBasisLabelKey,
  dateBasisValuesFor,
  defaultSubtypeForType,
  isDownloadPreviewAvailable,
  lockedDateBasis,
  paymentTypeLabelKey,
  productGrainLabelKey,
  reportModeLabelKey,
  reportLayoutLabelKey,
  resolvedCustomerRank,
  resolvedPaymentType,
  resolvedProductGrain,
  resolvedReportLayout,
  resolvedReportMode,
  resolvedUpsaleMode,
  showsCustomerRank,
  showsDateBasis,
  showsPaymentType,
  showsPermissionGroups,
  showsProductGrain,
  showsReportLayout,
  showsReportMode,
  showsSingleStockDate,
  showsEmployeeSearch,
  showsOrderSources,
  showsPartnerFilter,
  showsProductFilter,
  productFilterGrain,
  showsStatusFilter,
  showsUpsaleMode,
  showsWarehouseFilter,
  subtypeLabelKey,
  subtypesForType,
  typeLabelKey,
  upsaleModeLabelKey,
} from '../../helpers/downloadReportCatalog';

defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  canDownloadCsv: {
    type: Boolean,
    default: false,
  },
  isExporting: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['preview', 'clear', 'download']);
const reportType = defineModel('reportType', { type: String });
const reportSubtype = defineModel('reportSubtype', { type: String });
const dateBasis = defineModel('dateBasis', { type: String });
const paymentType = defineModel('paymentType', {
  type: String,
  default: DEFAULT_PAYMENT_TYPE,
});
const reportMode = defineModel('reportMode', {
  type: String,
  default: DEFAULT_REPORT_MODE,
});
const reportLayout = defineModel('reportLayout', {
  type: String,
  default: DEFAULT_REPORT_LAYOUT,
});
const upsaleMode = defineModel('upsaleMode', {
  type: String,
  default: DEFAULT_UPSALE_MODE,
});
const customerRank = defineModel('customerRank', {
  type: String,
  default: DEFAULT_CUSTOMER_RANK,
});
const stockDate = defineModel('stockDate', { type: String, default: '' });
const dateRange = defineModel('dateRange', { type: Array });
const rangeType = defineModel('rangeType', { type: String });
const orderSources = defineModel('orderSources', { type: Array });
const statusFilters = defineModel('statusFilters', {
  type: Array,
  default: () => [],
});
const employeeIds = defineModel('employeeIds', {
  type: Array,
  default: () => [],
});
const customRoleIds = defineModel('customRoleIds', {
  type: Array,
  default: () => [],
});
const warehouseId = defineModel('warehouseId', {
  default: null,
});
const partnerIds = defineModel('partnerIds', {
  type: Array,
  default: () => [],
});
const productIds = defineModel('productIds', {
  type: Array,
  default: () => [],
});

const { t, te } = useI18n();

const selectClass =
  'mt-1.5 block h-10 w-full rounded-lg bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand';

const employees = ref([]);
const permissionGroups = ref([]);
const warehouses = ref([]);
const partners = ref([]);
const products = ref([]);
const productGrainSelection = defineModel('productGrain', {
  type: String,
  default: DEFAULT_PRODUCT_GRAIN,
});
let employeesFetched = false;
let permissionGroupsFetched = false;
let warehousesFetched = false;
let partnersFetched = false;
let productsFetched = false;

const subtypeOptions = computed(() => subtypesForType(reportType.value));
const dateBasisOptions = computed(() =>
  dateBasisValuesFor(reportType.value, reportSubtype.value)
);

const showDateBasisField = computed(() =>
  showsDateBasis(reportType.value, reportSubtype.value)
);
const showPaymentTypeField = computed(() =>
  showsPaymentType(reportType.value, reportSubtype.value)
);
const showReportModeField = computed(() =>
  showsReportMode(reportType.value, reportSubtype.value)
);
const showUpsaleModeField = computed(() =>
  showsUpsaleMode(reportType.value, reportSubtype.value)
);
const showReportLayoutField = computed(() =>
  showsReportLayout(reportType.value, reportSubtype.value)
);
const showPermissionGroupField = computed(() =>
  showsPermissionGroups(reportType.value, reportSubtype.value)
);
const showCustomerRankField = computed(() =>
  showsCustomerRank(reportType.value, reportSubtype.value)
);
const showProductGrainField = computed(() =>
  showsProductGrain(reportType.value, reportSubtype.value)
);
const showFilterColumn = computed(
  () =>
    showDateBasisField.value ||
    showPaymentTypeField.value ||
    showReportModeField.value ||
    showReportLayoutField.value ||
    showUpsaleModeField.value ||
    showCustomerRankField.value ||
    showProductGrainField.value
);
const showSingleDateField = computed(() =>
  showsSingleStockDate(reportType.value, reportSubtype.value)
);
const dateRangeLabel = computed(() => {
  if (showSingleDateField.value) return t('DOWNLOAD_REPORTS.FORM.DATE');
  const locked = lockedDateBasis(reportType.value, reportSubtype.value);
  if (locked === 'creation') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_CREATION');
  }
  if (locked === 'approved') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_APPROVAL');
  }
  return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE');
});
const showSourcesField = computed(() =>
  showsOrderSources(reportType.value, reportSubtype.value)
);
const showStatusField = computed(() =>
  showsStatusFilter(reportType.value, reportSubtype.value)
);
const showProductionRangeHint = computed(
  () =>
    reportType.value === 'production' &&
    reportSubtype.value !== 'daily_product_wise_sales' &&
    isDownloadPreviewAvailable(reportType.value, reportSubtype.value)
);
const showDailyProductRangeHint = computed(
  () =>
    reportType.value === 'production' &&
    reportSubtype.value === 'daily_product_wise_sales'
);
const showCustomerRangeHint = computed(
  () =>
    reportType.value === 'customer' &&
    reportSubtype.value === 'product_sales_to_customer'
);
const showEmployeeField = computed(() => showsEmployeeSearch(reportType.value));
const showWarehouseField = computed(() =>
  showsWarehouseFilter(reportType.value, reportSubtype.value)
);
const showPartnerField = computed(() =>
  showsPartnerFilter(reportType.value, reportSubtype.value)
);
const showProductField = computed(() =>
  showsProductFilter(reportType.value, reportSubtype.value)
);
const productGrain = computed(() =>
  productFilterGrain(
    reportType.value,
    reportSubtype.value,
    productGrainSelection.value
  )
);
const showDamageRangeHint = computed(
  () => reportType.value === 'inventory' && reportSubtype.value === 'damage'
);
const showExpiryRangeHint = computed(
  () => reportType.value === 'inventory' && reportSubtype.value === 'expiries'
);
const showStockSnapshotHint = computed(
  () =>
    reportType.value === 'inventory' && reportSubtype.value === 'stock_summary'
);

const orderSourceLabel = source => {
  const key = `WEB_ORDER.FILTERS.ORDER_SOURCES.${String(source).toUpperCase()}`;
  return te(key) ? t(key) : source;
};

const sourceOptions = computed(() =>
  ORDER_SOURCES.map(source => ({
    value: source,
    label: orderSourceLabel(source),
  }))
);
const allSourcesSelected = computed(
  () =>
    orderSources.value.length === ORDER_SOURCES.length &&
    ORDER_SOURCES.every(source => orderSources.value.includes(source))
);
const sourceSummary = computed(() => {
  if (!orderSources.value.length || allSourcesSelected.value) {
    return t('DOWNLOAD_REPORTS.FORM.ALL_SOURCES');
  }
  if (orderSources.value.length === 1) {
    return orderSourceLabel(orderSources.value[0]);
  }
  return t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', {
    count: orderSources.value.length,
  });
});

const allStatusesSelected = computed(
  () =>
    statusFilters.value.length === ORDER_STATUSES.length &&
    ORDER_STATUSES.every(status => statusFilters.value.includes(status))
);
const someStatusesSelected = computed(
  () => statusFilters.value.length > 0 && !allStatusesSelected.value
);

const employeeOptions = computed(() =>
  employees.value.map(employee => ({
    value: Number(employee.id),
    label: employee.name,
  }))
);
const allEmployeesSelected = computed(
  () =>
    employees.value.length > 0 &&
    employeeIds.value.length === employees.value.length &&
    employees.value.every(employee =>
      employeeIds.value.includes(Number(employee.id))
    )
);
const employeeSummary = computed(() => {
  if (!employeeIds.value.length || allEmployeesSelected.value) {
    return t('DOWNLOAD_REPORTS.FORM.ALL_EMPLOYEES');
  }
  if (employeeIds.value.length === 1) {
    const match = employees.value.find(
      employee => Number(employee.id) === Number(employeeIds.value[0])
    );
    return (
      match?.name || t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', { count: 1 })
    );
  }
  return t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', {
    count: employeeIds.value.length,
  });
});

const permissionGroupOptions = computed(() =>
  permissionGroups.value.map(role => ({
    value: Number(role.id),
    label: role.name,
  }))
);
const permissionGroupSummary = computed(() => {
  if (!customRoleIds.value.length) {
    return t('DOWNLOAD_REPORTS.FORM.ALL_PERMISSION_GROUPS');
  }
  if (customRoleIds.value.length === 1) {
    const match = permissionGroups.value.find(
      role => Number(role.id) === Number(customRoleIds.value[0])
    );
    return (
      match?.name || t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', { count: 1 })
    );
  }
  return t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', {
    count: customRoleIds.value.length,
  });
});

const catalogType = product => {
  const meta = product?.metadata;
  const fromMeta =
    meta && typeof meta === 'object' ? meta.type || meta.product_type : '';
  return String(
    fromMeta || product?.product_type || product?.productType || 'simple'
  )
    .trim()
    .toLowerCase();
};

const variationParentId = product => {
  const meta = product?.metadata;
  if (!meta || typeof meta !== 'object') return 0;
  const id = Number(meta.parent_id ?? meta.parentId ?? 0);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const isVariationRecord = product =>
  catalogType(product) === 'variation' || variationParentId(product) > 0;

const matchesProductGrain = (product, grain) => {
  if (grain === 'variant') {
    if (isVariationRecord(product)) return true;
    return catalogType(product) !== 'variable';
  }
  return !isVariationRecord(product);
};

const productOptionLabel = (product, grain) => {
  const name = product.name || product.sku || String(product.id);
  if (grain !== 'variant') return name;
  const sku = String(product.sku || '').trim();
  return sku ? `${name} (${sku})` : name;
};

const productOptions = computed(() =>
  products.value
    .filter(product => matchesProductGrain(product, productGrain.value))
    .map(product => ({
      value: product.id,
      label: productOptionLabel(product, productGrain.value),
    }))
    .sort((left, right) => left.label.localeCompare(right.label))
);

const allProductsSelected = computed(
  () =>
    productOptions.value.length > 0 &&
    productIds.value.length === productOptions.value.length &&
    productOptions.value.every(option =>
      productIds.value.includes(option.value)
    )
);

const productSummary = computed(() => {
  if (!productIds.value.length || allProductsSelected.value) {
    return t('DOWNLOAD_REPORTS.FORM.ALL_PRODUCTS');
  }
  if (productIds.value.length === 1) {
    const match = productOptions.value.find(
      option => option.value === Number(productIds.value[0])
    );
    return (
      match?.label || t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', { count: 1 })
    );
  }
  return t('DOWNLOAD_REPORTS.FORM.SELECTED_COUNT', {
    count: productIds.value.length,
  });
});

const allPartnersSelected = computed(
  () =>
    partners.value.length > 0 &&
    partnerIds.value.length === partners.value.length &&
    partners.value.every(partner =>
      partnerIds.value.includes(Number(partner.id))
    )
);
const somePartnersSelected = computed(
  () => partnerIds.value.length > 0 && !allPartnersSelected.value
);

const statusLabel = status => {
  const key = `DOWNLOAD_REPORTS.TABLE.${String(status).replace(/-/g, '_').toUpperCase()}`;
  return te(key) ? t(key) : status;
};

const subtypeDisplayLabel = subtype => {
  const name = t(subtypeLabelKey(subtype));
  if (isDownloadPreviewAvailable(reportType.value, subtype)) return name;
  return t('DOWNLOAD_REPORTS.FORM.SUBTYPE_COMING_SOON', { name });
};

const isStatusSelected = status => statusFilters.value.includes(status);

const toggleStatus = status => {
  if (isStatusSelected(status)) {
    statusFilters.value = statusFilters.value.filter(item => item !== status);
    return;
  }
  statusFilters.value = [...statusFilters.value, status];
};

const toggleSelectAllStatuses = () => {
  statusFilters.value = allStatusesSelected.value ? [] : [...ORDER_STATUSES];
};

const isPartnerSelected = id => partnerIds.value.includes(Number(id));

const togglePartner = id => {
  const partnerId = Number(id);
  if (partnerIds.value.includes(partnerId)) {
    partnerIds.value = partnerIds.value.filter(item => item !== partnerId);
    return;
  }
  partnerIds.value = [...partnerIds.value, partnerId];
};

const toggleSelectAllPartners = () => {
  partnerIds.value = allPartnersSelected.value
    ? []
    : partners.value.map(partner => partner.id);
};

const partnerLabel = partner =>
  partner.provider ? `${partner.name} (${partner.provider})` : partner.name;

const onWarehouseChange = event => {
  const value = event.target.value;
  warehouseId.value = value ? Number(value) : null;
};

const onDateRangeChanged = value => {
  const [startDate, endDate, nextRangeType] = value || [];
  dateRange.value = [startDate, endDate];
  if (nextRangeType) rangeType.value = nextRangeType;
};

const optionList = payload => {
  const list = Array.isArray(payload) ? payload : [];
  return list
    .map(item => ({
      id: Number(item.id),
      name: item.name || '',
      provider: item.provider || '',
    }))
    .filter(item => Number.isFinite(item.id) && item.id > 0);
};

const loadWarehouses = async () => {
  if (warehousesFetched) return;
  warehousesFetched = true;
  try {
    const { data } = await inventoryWarehousesAPI.get();
    const payload = data?.payload ?? data;
    warehouses.value = optionList(payload).map(warehouse => ({
      id: warehouse.id,
      name: warehouse.name,
    }));
  } catch {
    warehouses.value = [];
  }
};

const loadPartners = async () => {
  if (partnersFetched) return;
  partnersFetched = true;
  try {
    const { data } = await deliveryPartnerAccountsAPI.get();
    const payload = data?.payload ?? data;
    partners.value = optionList(payload);
  } catch {
    partners.value = [];
  }
};

const loadEmployees = async () => {
  if (employeesFetched) return;
  employeesFetched = true;
  try {
    const { data } = await webOrdersAPI.assignableAgents();
    const payload = data?.payload ?? data;
    const list = Array.isArray(payload?.agents) ? payload.agents : [];
    employees.value = list
      .map(agent => ({
        id: Number(agent.id),
        name: agent.name || '',
      }))
      .filter(agent => Number.isFinite(agent.id) && agent.id > 0);
  } catch {
    employees.value = [];
  }
};

const productPage = async page => {
  const { data } = await webProductsAPI.list({ page, per_page: 300 });
  const payload = data?.payload ?? data;
  const list = Array.isArray(payload) ? payload : [];
  const meta = data?.meta || {};
  const totalPages = Number(meta.total_pages ?? meta.totalPages ?? 1) || 1;
  return { list, totalPages };
};

const loadProducts = async () => {
  if (productsFetched) return;
  productsFetched = true;
  try {
    const first = await productPage(1);
    const rest = await Promise.all(
      Array.from({ length: Math.max(first.totalPages - 1, 0) }, (_, index) =>
        productPage(index + 2)
      )
    );
    const collected = [...first.list, ...rest.flatMap(page => page.list)];
    products.value = collected
      .map(product => ({
        id: Number(product.id),
        name: product.name || '',
        sku: product.sku || '',
        product_type: product.product_type || product.productType || '',
        metadata: product.metadata || {},
      }))
      .filter(product => Number.isFinite(product.id) && product.id > 0);
  } catch {
    products.value = [];
  }
};

const loadPermissionGroups = async () => {
  if (permissionGroupsFetched) return;
  permissionGroupsFetched = true;
  try {
    const { data } = await CustomRoleAPI.get();
    const list = Array.isArray(data) ? data : [];
    permissionGroups.value = list
      .map(role => ({
        id: Number(role.id),
        name: role.name || '',
      }))
      .filter(role => Number.isFinite(role.id) && role.id > 0);
  } catch {
    permissionGroups.value = [];
  }
};

watch(reportType, async type => {
  if (!subtypesForType(type).includes(reportSubtype.value)) {
    reportSubtype.value = defaultSubtypeForType(type);
  }
  if (type === 'user') {
    await loadEmployees();
  }
});

watch([reportType, reportSubtype], () => {
  productIds.value = [];
  productGrainSelection.value = DEFAULT_PRODUCT_GRAIN;
  if (!REPORT_MODES.includes(reportMode.value)) {
    reportMode.value = resolvedReportMode(reportMode.value);
  }
  if (!UPSALE_MODES.includes(upsaleMode.value)) {
    upsaleMode.value = resolvedUpsaleMode(upsaleMode.value);
  }
  if (!CUSTOMER_RANKS.includes(customerRank.value)) {
    customerRank.value = resolvedCustomerRank(customerRank.value);
  }
  if (!REPORT_LAYOUTS.includes(reportLayout.value)) {
    reportLayout.value = resolvedReportLayout(reportLayout.value);
  }
  if (!PAYMENT_TYPES.includes(paymentType.value)) {
    paymentType.value = resolvedPaymentType(paymentType.value);
  }
  const locked = lockedDateBasis(reportType.value, reportSubtype.value);
  if (locked) {
    dateBasis.value = locked;
    return;
  }
  const allowed = dateBasisValuesFor(reportType.value, reportSubtype.value);
  if (allowed.length && !allowed.includes(dateBasis.value)) {
    dateBasis.value = allowed[0];
  }
});

watch(showWarehouseField, shown => {
  if (shown) loadWarehouses();
});

watch(showPartnerField, shown => {
  if (shown) loadPartners();
});

watch(showProductField, shown => {
  if (shown) loadProducts();
});

watch(productGrainSelection, () => {
  if (!PRODUCT_GRAINS.includes(productGrainSelection.value)) {
    productGrainSelection.value = resolvedProductGrain(
      productGrainSelection.value
    );
    return;
  }
  productIds.value = [];
});

watch(showPermissionGroupField, shown => {
  if (shown) loadPermissionGroups();
});
</script>

<template>
  <section
    class="rounded-xl bg-n-solid-2 px-6 py-5 shadow outline outline-1 outline-n-container"
  >
    <form class="flex flex-col gap-6" @submit.prevent="emit('preview')">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label
            class="text-sm font-medium text-n-slate-12"
            for="download-report-type"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.REPORT_TYPE') }}
          </label>
          <select
            id="download-report-type"
            v-model="reportType"
            :class="selectClass"
          >
            <option
              v-for="type in DOWNLOAD_REPORT_TYPES"
              :key="type.value"
              :value="type.value"
            >
              {{ t(typeLabelKey(type.value)) }}
            </option>
          </select>
        </div>

        <div>
          <label
            class="text-sm font-medium text-n-slate-12"
            for="download-report-subtype"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.SUBTYPE') }}
          </label>
          <select
            id="download-report-subtype"
            v-model="reportSubtype"
            :class="selectClass"
          >
            <option
              v-for="subtype in subtypeOptions"
              :key="subtype"
              :value="subtype"
            >
              {{ subtypeDisplayLabel(subtype) }}
            </option>
          </select>
        </div>
      </div>

      <div class="grid gap-6" :class="showFilterColumn ? 'lg:grid-cols-2' : ''">
        <div v-if="showFilterColumn" class="flex min-w-0 flex-col gap-6">
          <fieldset v-if="showReportModeField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.REPORT_MODE') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="mode in REPORT_MODES"
                :key="mode"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  reportMode === mode
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="reportMode"
                  type="radio"
                  name="download-report-mode"
                  class="size-4 accent-n-brand"
                  :value="mode"
                />
                {{ t(reportModeLabelKey(mode)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showDateBasisField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.DATE_BASIS') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="basis in dateBasisOptions"
                :key="basis"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  dateBasis === basis
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="dateBasis"
                  type="radio"
                  name="download-report-date-basis"
                  class="size-4 accent-n-brand"
                  :value="basis"
                />
                {{ t(dateBasisLabelKey(basis)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showProductGrainField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.PRODUCT_GRAIN') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="grain in PRODUCT_GRAINS"
                :key="grain"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  productGrainSelection === grain
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="productGrainSelection"
                  type="radio"
                  name="download-report-product-grain"
                  class="size-4 accent-n-brand"
                  :value="grain"
                />
                {{ t(productGrainLabelKey(grain)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showPaymentTypeField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.PAYMENT_TYPE') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="type in PAYMENT_TYPES"
                :key="type"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  paymentType === type
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="paymentType"
                  type="radio"
                  name="download-report-payment-type"
                  class="size-4 accent-n-brand"
                  :value="type"
                />
                {{ t(paymentTypeLabelKey(type)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showUpsaleModeField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.UPSALE_MODE') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="mode in UPSALE_MODES"
                :key="mode"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  upsaleMode === mode
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="upsaleMode"
                  type="radio"
                  name="download-report-upsale-mode"
                  class="size-4 accent-n-brand"
                  :value="mode"
                />
                {{ t(upsaleModeLabelKey(mode)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showCustomerRankField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.CUSTOMER_RANK') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="rank in CUSTOMER_RANKS"
                :key="rank"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  customerRank === rank
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="customerRank"
                  type="radio"
                  name="download-report-customer-rank"
                  class="size-4 accent-n-brand"
                  :value="rank"
                />
                {{ t(customerRankLabelKey(rank)) }}
              </label>
            </div>
          </fieldset>

          <fieldset v-if="showReportLayoutField" class="min-w-0">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('DOWNLOAD_REPORTS.FORM.REPORT_LAYOUT') }}
            </legend>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                v-for="layout in REPORT_LAYOUTS"
                :key="layout"
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
                :class="
                  reportLayout === layout
                    ? 'border-n-brand bg-n-brand/5 text-n-brand'
                    : 'hover:border-n-slate-6'
                "
              >
                <input
                  v-model="reportLayout"
                  type="radio"
                  name="download-report-layout"
                  class="size-4 accent-n-brand"
                  :value="layout"
                />
                {{ t(reportLayoutLabelKey(layout)) }}
              </label>
            </div>
          </fieldset>
        </div>

        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">
            {{ dateRangeLabel }}
          </p>
          <div v-if="showSingleDateField" class="mt-3">
            <input
              v-model="stockDate"
              type="date"
              class="block h-10 w-full max-w-xs rounded-lg bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            />
          </div>
          <div v-else class="mt-3">
            <WootDatePicker
              v-model:date-range="dateRange"
              v-model:range-type="rangeType"
              compact
              with-time
              align="end"
              :ranges="overviewDateRanges"
              @date-range-changed="onDateRangeChanged"
            />
          </div>
          <p
            v-if="showProductionRangeHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.PRODUCTION_RANGE_HINT') }}
          </p>
          <p
            v-if="showDailyProductRangeHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.DAILY_PRODUCT_RANGE_HINT') }}
          </p>
          <p
            v-if="showCustomerRangeHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.CUSTOMER_RANGE_HINT') }}
          </p>
          <p
            v-if="showDamageRangeHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.DAMAGE_RANGE_HINT') }}
          </p>
          <p
            v-if="showExpiryRangeHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.EXPIRY_RANGE_HINT') }}
          </p>
          <p
            v-if="showStockSnapshotHint"
            class="mb-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('DOWNLOAD_REPORTS.FORM.STOCK_SNAPSHOT_HINT') }}
          </p>
        </div>
      </div>

      <div v-if="showWarehouseField">
        <label
          class="text-sm font-medium text-n-slate-12"
          for="download-report-warehouse"
        >
          {{ t('DOWNLOAD_REPORTS.FORM.WAREHOUSE') }}
        </label>
        <select
          id="download-report-warehouse"
          :value="
            warehouseId == null || warehouseId === '' ? '' : String(warehouseId)
          "
          :class="selectClass"
          @change="onWarehouseChange"
        >
          <option value="">
            {{ t('DOWNLOAD_REPORTS.FORM.ALL_WAREHOUSES') }}
          </option>
          <option
            v-for="warehouse in warehouses"
            :key="warehouse.id"
            :value="String(warehouse.id)"
          >
            {{ warehouse.name }}
          </option>
        </select>
      </div>

      <DownloadReportCheckDropdown
        v-if="showSourcesField"
        v-model="orderSources"
        :label="t('DOWNLOAD_REPORTS.FORM.SOURCES')"
        :hint="t('DOWNLOAD_REPORTS.FORM.SOURCES_HINT')"
        :summary="sourceSummary"
        :select-all-label="t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_SOURCES')"
        :options="sourceOptions"
      />

      <fieldset v-if="showStatusField">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('DOWNLOAD_REPORTS.FORM.STATUSES') }}
          </p>
          <label
            class="inline-flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
          >
            <Checkbox
              :model-value="allStatusesSelected"
              :indeterminate="someStatusesSelected"
              @change="toggleSelectAllStatuses"
            />
            {{ t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_STATUSES') }}
          </label>
        </div>
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('DOWNLOAD_REPORTS.FORM.STATUSES_HINT') }}
        </p>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          <label
            v-for="status in ORDER_STATUSES"
            :key="status"
            class="inline-flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-2"
          >
            <Checkbox
              :model-value="isStatusSelected(status)"
              @change="toggleStatus(status)"
            />
            {{ statusLabel(status) }}
          </label>
        </div>
      </fieldset>

      <fieldset v-if="showPartnerField">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('DOWNLOAD_REPORTS.FORM.PARTNERS') }}
          </p>
          <label
            class="inline-flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
          >
            <Checkbox
              :model-value="allPartnersSelected"
              :indeterminate="somePartnersSelected"
              @change="toggleSelectAllPartners"
            />
            {{ t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_PARTNERS') }}
          </label>
        </div>
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('DOWNLOAD_REPORTS.FORM.PARTNERS_HINT') }}
        </p>
        <div
          v-if="partners.length"
          class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4"
        >
          <label
            v-for="partner in partners"
            :key="partner.id"
            class="inline-flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-2"
          >
            <Checkbox
              :model-value="isPartnerSelected(partner.id)"
              @change="togglePartner(partner.id)"
            />
            {{ partnerLabel(partner) }}
          </label>
        </div>
      </fieldset>

      <DownloadReportCheckDropdown
        v-if="showProductField"
        v-model="productIds"
        :label="t('DOWNLOAD_REPORTS.FORM.PRODUCT')"
        :hint="t('DOWNLOAD_REPORTS.FORM.PRODUCTS_HINT')"
        :summary="productSummary"
        :select-all-label="t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_PRODUCTS')"
        :options="productOptions"
      />

      <DownloadReportCheckDropdown
        v-if="showEmployeeField"
        v-model="employeeIds"
        :label="t('DOWNLOAD_REPORTS.FORM.EMPLOYEES')"
        :hint="t('DOWNLOAD_REPORTS.FORM.EMPLOYEES_HINT')"
        :summary="employeeSummary"
        :select-all-label="t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_EMPLOYEES')"
        :options="employeeOptions"
      />

      <DownloadReportCheckDropdown
        v-if="showPermissionGroupField"
        v-model="customRoleIds"
        :label="t('DOWNLOAD_REPORTS.FORM.PERMISSION_GROUPS')"
        :hint="t('DOWNLOAD_REPORTS.FORM.PERMISSION_GROUPS_HINT')"
        :summary="permissionGroupSummary"
        :select-all-label="
          t('DOWNLOAD_REPORTS.FORM.SELECT_ALL_PERMISSION_GROUPS')
        "
        :options="permissionGroupOptions"
      />

      <div class="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          color="slate"
          :label="t('DOWNLOAD_REPORTS.ACTIONS.CLEAR')"
          @click="emit('clear')"
        />
        <Button
          type="submit"
          :label="t('DOWNLOAD_REPORTS.ACTIONS.PREVIEW')"
          :is-loading="isLoading"
          :disabled="isLoading"
        />
        <span
          v-tooltip.top="
            canDownloadCsv
              ? t('DOWNLOAD_REPORTS.ACTIONS.DOWNLOAD_CSV_TOOLTIP')
              : t('DOWNLOAD_REPORTS.ACTIONS.DOWNLOAD_CSV_DISABLED_TOOLTIP')
          "
        >
          <Button
            type="button"
            variant="outline"
            color="slate"
            :disabled="!canDownloadCsv || isLoading || isExporting"
            :is-loading="isExporting"
            :label="t('DOWNLOAD_REPORTS.ACTIONS.DOWNLOAD_CSV')"
            @click="emit('download')"
          />
        </span>
      </div>
    </form>
  </section>
</template>
